<div class="upload-images-modal">
  <div mat-dialog-titled>
    <h2>
      {{ modalTitleKey | translate: { amount: $safeNavigationMigration(selectedFiles?.length) } }}
    </h2>
  </div>

  @if (loading) {
    <mat-dialog-content class="mat-typography content-loading">
      <mat-progress-spinner mode="indeterminate" aria-label="Loading"></mat-progress-spinner>
    </mat-dialog-content>
  }

  @if (!loading) {
    <mat-dialog-content class="mat-typography">
      <!-- File Upload Area -->
      <div
        class="upload-area"
        [class.drag-over]="isDragOver"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        (click)="triggerFileInput()">
        <input
          type="file"
          id="upload-multiple-images-dialog-file-input"
          multiple
          [accept]="supportsVideos ? 'image/*, video/mp4, video/webm' : 'image/*'"
          (change)="onFileSelected($event)"
          style="display: none" />
        <div class="upload-content">
          <i class="material-icons upload-icon" [style.color]="isDragOver ? 'var(--linkColor)' : ''">cloud_upload</i>
          <div class="upload-text">
            @if (supportsVideos) {
              <div class="primary-text">{{ "campaign.widget.sidebar.media.upload-images.drag-drop-hint-videos" | translate }}</div>
              <div class="secondary-text">{{ "campaign.widget.sidebar.media.upload-images.drag-drop-hint-fileformats" | translate }}</div>
            } @else {
              <div class="primary-text">{{ "campaign.widget.sidebar.media.upload-images.drag-drop-hint" | translate }}</div>
              <div class="secondary-text">{{ "campaign.widget.sidebar.media.upload-images.select-multiple-hint" | translate }}</div>
            }
          </div>
        </div>
      </div>
      <!-- Selected Images List -->
      @if (selectedFiles.length > 0) {
        <div class="selected-images">
          @if (supportsVideos) {
            <div class="section-title">{{ "campaign.widget.sidebar.media.upload-videos.selected-amount" | translate }} ({{ selectedFiles.length }})</div>
          } @else {
            <div class="section-title">{{ "campaign.widget.sidebar.media.upload-images.selected-amount" | translate }} ({{ selectedFiles.length }})</div>
          }
          <div class="image-list">
            @for (selectedFile of selectedFiles; track selectedFile; let i = $index) {
              <div class="image-item">
                <div class="image-preview checkerboard">
                  @if (selectedFile.processingType === mediaProcessingType.Video) {
                    <div class="video-placeholder">
                      <mat-icon class="video-icon">play_circle_filled</mat-icon>
                    </div>
                  } @else {
                    <img [src]="selectedFile.preview" [alt]="selectedFile.file.name" />
                  }
                </div>
                <div class="image-details">
                  <div class="image-name">{{ selectedFile.file.name }}</div>
                  @if (selectedFile.hasError) {
                    <div class="image-errors">{{ selectedFile.errorMessage | translate }}</div>
                  }
                  <div class="image-size">{{ selectedFile.sizeKB }} KB</div>
                  <!-- Video info text -->
                  @if (selectedFile.processingType === mediaProcessingType.Video) {
                    <p class="video-info-text">
                      <mat-icon class="center">info</mat-icon>
                      {{ "campaign.widget.sidebar.media.upload-videos.video-info-text" | translate }}
                    </p>
                  }
                  <!-- Processing Options -->
                  @if (selectedFile.showResizeOption) {
                    <div class="processing-options">
                      <div class="label">
                        <mat-icon class="center">info</mat-icon>
                        {{ "campaign.widget.sidebar.media.upload-images.large-file" | translate }}
                      </div>
                      <mat-radio-group [value]="selectedFile.processingType" (change)="onProcessingTypeChange(i, $event.value)">
                        <mat-radio-button [value]="mediaProcessingType.Resized">
                          {{ "campaign.widget.sidebar.media.upload-images.resize-text" | translate }}
                        </mat-radio-button>
                        <mat-radio-button [value]="mediaProcessingType.Original">
                          {{ "campaign.widget.sidebar.media.upload-images.original-size" | translate }}
                        </mat-radio-button>
                      </mat-radio-group>
                    </div>
                  }
                </div>
                <div class="image-actions">
                  <button mat-icon-button class="remove-button" (click)="removeFile(i)">
                    <i class="material-icons">close</i>
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      }
      <!-- Container Selection -->
      @if (selectedFiles.length > 0) {
        <div class="container-selection">
          <div class="path-options">
            @if (supportsVideos) {
              <div class="label">{{ "campaign.widget.sidebar.media.category-image" | translate }}</div>
            } @else {
              <div class="label">{{ "campaign.widget.sidebar.media.category" | translate }}</div>
            }
            <mat-radio-group [(ngModel)]="category" (change)="onCategoryChange($event)">
              <mat-radio-button [value]="MediaCategory.Photos" checked>
                {{ "campaign.widget.sidebar.media.photos" | translate }}
              </mat-radio-button>
              <mat-radio-button [value]="MediaCategory.Graphics">
                {{ "campaign.widget.sidebar.media.graphics" | translate }}
              </mat-radio-button>
            </mat-radio-group>
            @if (supportsVideos) {
              <div class="info-text">
                <span>{{ "campaign.widget.sidebar.media.upload-videos.video-categorize" | translate }}</span>
              </div>
            }
            <div class="label">{{ "campaign.widget.sidebar.media.collection" | translate }}</div>
            <mat-form-field class="input-full-width" appearance="outline" [matTooltip]="collectionDisabledTooltip | translate">
              <mat-label>{{ "campaign.widget.sidebar.media.collection" | translate }} ({{ "generic.optional" | translate }})</mat-label>
              <input type="text" matInput [matAutocomplete]="auto" [formControl]="collectionControl" />
              <mat-autocomplete #auto="matAutocomplete">
                <mat-option [value]="null">{{ "generic.none" | translate }}</mat-option>
                @for (collection of filteredOptions | async; track collection) {
                  <mat-option [value]="collection">
                    {{ collection }}
                  </mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
          </div>
        </div>
      }
    </mat-dialog-content>
  }

  @if (!loading) {
    <mat-dialog-actions align="end">
      <button mat-stroked-button class="secondary-button" (click)="cancel()">
        {{ "generic.cancel" | translate }}
      </button>
      @if (supportsVideos) {
        <button mat-stroked-button class="primary-button" [disabled]="selectedFiles.length === 0" (click)="uploadMediaFiles()">
          {{ "campaign.widget.sidebar.media.upload-videos.dialog-accept" | translate }} ({{ selectedFiles.length }})
        </button>
      } @else {
        <button mat-stroked-button class="primary-button" [disabled]="selectedFiles.length === 0" (click)="uploadMediaFiles()">
          {{ "campaign.widget.sidebar.media.upload-images.dialog-accept" | translate }} ({{ selectedFiles.length }})
        </button>
      }
    </mat-dialog-actions>
  }
</div>
